import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
} from "@mui/material";
import { Search, Settings } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import SubscriptionAdminService from "../../../services/SubscriptionAdminService";

const STATUS_COLORS = {
  ACTIVE: "success",
  FREE: "default",
  PENDING_PAYMENT: "warning",
  EXPIRED: "error",
  CANCELLED: "error",
};

/** Format a LocalDate string (YYYY-MM-DD) as e.g. "11 Jun 2026". */
const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

/** Whole days from an expiry date (YYYY-MM-DD) to today; never negative. */
const daysSince = (iso) => {
  const then = new Date(`${iso}T00:00:00`);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor((now - then) / 86400000));
};

/** Human label for how long ago a plan expired — days under 2 months, then months. */
const expiredSinceLabel = (iso) => {
  const d = daysSince(iso);
  if (d === 0) return "Expired today";
  if (d < 60) return `Expired ${d} day${d > 1 ? "s" : ""} ago`;
  const months = Math.floor(d / 30);
  return `Expired ${months} month${months > 1 ? "s" : ""} ago`;
};

/** ROOT_ADMIN list of customer organisations with their current subscription state. */
const OrganizationSubscriptionsList = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    SubscriptionAdminService.getSummaries()
      .then((list) => {
        setRows(Array.isArray(list) ? list : []);
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load organisation subscriptions");
        setLoading(false);
      });
  }, []);

  const filtered = rows.filter((r) =>
    (r.organisationName || "").toLowerCase().includes(query.toLowerCase())
  );

  // Column 3 (Expiry Date): an active paid plan shows its own expiry; a free org
  // that previously had a paid plan shows when that plan expired; a never-purchased
  // free org has no expiry to show.
  const renderExpiry = (r) => {
    if (r.status === "ACTIVE") {
      return r.currentPeriodEnd ? formatDate(r.currentPeriodEnd) : "Open-ended";
    }
    if (r.expiredOn) return formatDate(r.expiredOn);
    return "—";
  };

  // Column 4 (Duration): only meaningful when on the free tier — either how long
  // since a paid plan lapsed, or that no plan was ever purchased.
  const renderDuration = (r) => {
    if (r.status === "ACTIVE") {
      return <Typography variant="body2" color="textSecondary">—</Typography>;
    }
    if (r.expiredOn) {
      return (
        <Typography variant="body2" sx={{ color: "error.main", fontWeight: 600 }}>
          {expiredSinceLabel(r.expiredOn)}
          {r.expiredPlanName ? ` (${r.expiredPlanName})` : ""}
        </Typography>
      );
    }
    return (
      <Typography variant="body2" color="textSecondary">
        Not purchased yet
      </Typography>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardContent>
          <Typography variant="h5" sx={{ mb: 2 }}>Organisation Subscriptions</Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <TextField
            fullWidth
            placeholder="Search organisations…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Organisation</TableCell>
                    <TableCell>Current Plan</TableCell>
                    <TableCell>Expiry Date</TableCell>
                    <TableCell>Status / Duration</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">No organisations found.</TableCell>
                    </TableRow>
                  )}
                  {filtered.map((r) => (
                    <TableRow key={r.organisationId} hover>
                      <TableCell>{r.organisationName}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={r.currentPlanName || "Free"}
                          color={STATUS_COLORS[r.status] || "default"}
                        />
                      </TableCell>
                      <TableCell>{renderExpiry(r)}</TableCell>
                      <TableCell>{renderDuration(r)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Manage subscription">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`view/${r.organisationId}`)}
                          >
                            <Settings fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default OrganizationSubscriptionsList;
